import type {
  AvailabilityStatus,
  CapacityBand,
  CapacityBasis,
  Fact,
  GridConnectionStatus,
  IntroductionRole,
  IntroductionTiming,
  MacroRegion,
  MunicipalApprovalStatus,
  NetworkCategory,
  PowerCategory,
  PublicListing,
  ServiceCategory,
  SiteCategory,
} from './types';

export const siteCategoryLabel: Record<SiteCategory, string> = {
  operational_datacenter: 'Operational datacenter',
  datacenter_shell: 'Datacenter shell',
  industrial_conversion: 'Industrial conversion',
  powered_land: 'Powered land',
  edge_site: 'Edge site',
};

export const capacityBandLabel: Record<CapacityBand, string> = {
  under_1_mw: 'Under 1 MW',
  '1_to_5_mw': '1 to 5 MW',
  '5_to_20_mw': '5 to 20 MW',
  '20_to_50_mw': '20 to 50 MW',
  over_50_mw: 'Over 50 MW',
};

export const capacityBasisLabel: Record<CapacityBasis, string> = {
  confirmed: 'Confirmed by site side',
  estimated: 'Our estimate',
};

export const serviceCategoryLabel: Record<ServiceCategory, string> = {
  colocation: 'Colocation',
  wholesale_lease: 'Wholesale lease',
  powered_shell: 'Powered shell',
  build_to_suit: 'Build to suit',
  site_sale: 'Site sale',
  land_sale: 'Land sale',
};

export const powerCategoryLabel: Record<PowerCategory, string> = {
  grid_connected: 'Grid connected',
  dedicated_substation: 'Dedicated substation',
  dual_feed: 'Dual feed',
  renewable_supply: 'Renewable supply',
  onsite_generation: 'Onsite generation',
};

export const networkCategoryLabel: Record<NetworkCategory, string> = {
  carrier_neutral: 'Carrier neutral',
  multiple_carriers: 'Multiple carriers',
  dark_fibre: 'Dark fibre',
  internet_exchange_nearby: 'Internet exchange nearby',
  subsea_landing_nearby: 'Subsea cable landing nearby',
};

export const gridConnectionLabel: Record<GridConnectionStatus, string> = {
  energised: 'Energised',
  contracted: 'Connection contracted',
  application_submitted: 'Application submitted',
  not_started: 'Not started',
};

export const municipalApprovalLabel: Record<MunicipalApprovalStatus, string> = {
  granted: 'Permit granted',
  in_progress: 'Permit in progress',
  pre_application: 'Pre application talks',
  not_required: 'Not required',
  not_started: 'Not started',
};

export const availabilityLabel: Record<AvailabilityStatus, string> = {
  available_now: 'Available now',
  available_from: 'Available from',
  under_discussion: 'Under discussion',
  on_request: 'On request',
};

export const macroRegionLabel: Record<MacroRegion, string> = {
  nordics: 'Nordics',
  uk_ireland: 'UK and Ireland',
  benelux: 'Benelux',
  dach: 'Germany, Austria, Switzerland',
  france: 'France',
  iberia: 'Iberia',
  italy: 'Italy',
  central_eastern: 'Central and Eastern Europe',
};

export const macroRegionShort: Record<MacroRegion, string> = {
  nordics: 'Nordics',
  uk_ireland: 'UK & IE',
  benelux: 'Benelux',
  dach: 'DACH',
  france: 'France',
  iberia: 'Iberia',
  italy: 'Italy',
  central_eastern: 'CEE',
};

export const roleLabel: Record<IntroductionRole, string> = {
  capacity_seeker: 'I need capacity or a site',
  owner_or_operator: 'I own or operate a site',
  adviser: 'I advise a buyer or seller',
  other: 'Other',
};

export const timingLabel: Record<IntroductionTiming, string> = {
  within_6_months: 'Within 6 months',
  '6_to_12_months': '6 to 12 months',
  '12_to_24_months': '12 to 24 months',
  exploring: 'Exploring, no date yet',
};

const countryName: Record<string, string> = {
  AT: 'Austria',
  BE: 'Belgium',
  CH: 'Switzerland',
  CZ: 'Czechia',
  DE: 'Germany',
  DK: 'Denmark',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  IE: 'Ireland',
  IS: 'Iceland',
  IT: 'Italy',
  LU: 'Luxembourg',
  NL: 'Netherlands',
  NO: 'Norway',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SE: 'Sweden',
};

const countryMacroRegion: Record<string, MacroRegion> = {
  NO: 'nordics',
  SE: 'nordics',
  FI: 'nordics',
  DK: 'nordics',
  IS: 'nordics',
  GB: 'uk_ireland',
  IE: 'uk_ireland',
  NL: 'benelux',
  BE: 'benelux',
  LU: 'benelux',
  DE: 'dach',
  AT: 'dach',
  CH: 'dach',
  FR: 'france',
  ES: 'iberia',
  PT: 'iberia',
  IT: 'italy',
  PL: 'central_eastern',
  CZ: 'central_eastern',
  RO: 'central_eastern',
};

export function countryLabel(code: string): string {
  return countryName[code] ?? code;
}

export function macroRegionOf(listing: Pick<PublicListing, 'country_code'>): MacroRegion | null {
  return countryMacroRegion[listing.country_code] ?? null;
}

export function placeLabel(listing: PublicListing): string {
  const parts = [listing.coarse_locality, listing.region, countryLabel(listing.country_code)];
  return parts.filter((p): p is string => Boolean(p)).join(', ');
}

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const monthFormat = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatDate(iso: string): string {
  return dateFormat.format(new Date(iso));
}

export function formatMonth(iso: string): string {
  return monthFormat.format(new Date(iso));
}

export function availabilityHeadline(listing: PublicListing): string {
  const a = listing.availability;
  if (a.status === 'available_from' && a.available_from) {
    return `Available from ${formatMonth(a.available_from)}`;
  }
  return availabilityLabel[a.status];
}

export const disclosureLabel = {
  unknown: 'Unknown',
  not_disclosed: 'Not disclosed',
} as const;

export const disclosureHint = {
  unknown: 'Not yet confirmed by anyone. We can try to find out.',
  not_disclosed: 'Known to us, shared only after an agreed introduction.',
} as const;

/** Formats a fact, or returns the disclosure label when no value is public. */
export function factText<T>(fact: Fact<T>, format: (value: T) => string): string {
  return fact.disclosure === 'known' ? format(fact.value) : disclosureLabel[fact.disclosure];
}

export function listText<T extends string>(values: T[], labels: Record<T, string>): string {
  return values.length === 0 ? 'None' : values.map((v) => labels[v]).join(', ');
}
